import { compact, range } from "lodash";
import { useState } from "react";
import {
  AssessmentQuestionResponseVisibility,
  AssessmentQuestionResponses,
  OrganizationTalentIndicatorFragment,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import FormFields from "@components/form/form-fields";
import ToggleChevronIcon from "@components/layout/toggle-chevron-icon";
import DeprecatedSelect from "@components/select/select";
import ToggleSwitch from "@components/toggle-switch/toggle-switch";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { alphabet, assessmentQuestionCommentOptions } from "@helpers/constants";

import { TalentIndicatorForm } from "./talent-indicator-sidebar";

const TalentIndicatorSidebarIndicator = ({
  talentIndicator,
  scale,
  canUpdate,
  sectionIndex,
  questionIndex,
  isFirstQuestion,
  isLastQuestion,
  onUpdateQuestion,
  onMoveQuestionUp,
  onMoveQuestionDown,
}: {
  talentIndicator: TalentIndicatorForm;
  // The ratings the question offers, set in the organization's settings.
  scale?: Pick<
    OrganizationTalentIndicatorFragment,
    "scaleStartsAt" | "scale" | "labels" | "labelDescriptions"
  >;
  canUpdate: boolean;
  sectionIndex: number;
  questionIndex: number;
  isFirstQuestion: boolean;
  isLastQuestion: boolean;
  onUpdateQuestion: (data: TalentIndicatorForm) => void;
  onMoveQuestionUp: () => void;
  onMoveQuestionDown: () => void;
}) => {
  const label = useLabel();
  const [isShowingSettings, setIsShowingSettings] = useState(false);

  return (
    <div className="bg-white border rounded-sm">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-3">
            <div className="py-0.5 px-2 rounded-sm bg-green-100 font-bold">{`${
              alphabet[sectionIndex]
            }${questionIndex + 1}`}</div>
            <div className="font-medium">{talentIndicator.title}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              disabled={!canUpdate || isFirstQuestion}
              icon={TopicflowIconMap.upArrow}
              theme={buttonTheme.iconGray}
              onClick={onMoveQuestionUp}
            />
            <Button
              disabled={!canUpdate || isLastQuestion}
              icon={TopicflowIconMap.downArrow}
              theme={buttonTheme.iconGray}
              onClick={onMoveQuestionDown}
            />
            <ToggleSwitch
              aria-label="Talent indicator toggle"
              disabled={!canUpdate}
              checked={talentIndicator.enabled}
              onChange={(enabled) => {
                onUpdateQuestion({ ...talentIndicator, enabled });
              }}
            />
          </div>
        </div>
      </div>

      {(!isEmptyValue(talentIndicator.description) || scale) && (
        <div className="px-4 py-3 border-t">
          <FormFields
            fields={compact([
              !isEmptyValue(talentIndicator.description) && {
                title: "Description",
                render: () => (
                  <TextareaWysiwyg
                    editable={false}
                    placeholder="Add decription (optional)"
                    value={talentIndicator.description}
                    deps={[talentIndicator.title]}
                  />
                ),
              },
              scale && {
                title: "Range",
                render: () => (
                  // Read only, like the description: the ratings are set in
                  // the organization's settings. Hovering one explains it.
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                    {range(scale.scale).map((index) => (
                      <span
                        key={index}
                        title={scale.labelDescriptions[index] || undefined}
                      >
                        <span className="mr-1.5 text-gray-400">
                          {scale.scaleStartsAt + index}
                        </span>
                        {scale.labels[index]}
                      </span>
                    ))}
                  </div>
                ),
              },
            ])}
          />
        </div>
      )}

      <div className="px-4 py-2 border-t">
        <Button
          theme={buttonTheme.text}
          className="flex items-center gap-2 font-semibold -ml-[10px]"
          onClick={() => setIsShowingSettings(!isShowingSettings)}
        >
          <div>Settings</div>
          <ToggleChevronIcon expanded={isShowingSettings} />
        </Button>

        {isShowingSettings && talentIndicator.enabled && (
          <div className="mt-4 mb-2">
            <FormFields
              fields={compact([
                !talentIndicator.isManagerIndicator && {
                  title: "Responders",
                  render: () => (
                    <DeprecatedSelect
                      disabled={!canUpdate}
                      value={talentIndicator.responses}
                      options={compact([
                        {
                          label: "Both Manager and Subject",
                          description: "Everyone answers this question",
                          value: AssessmentQuestionResponses.Both,
                        },
                        talentIndicator.responseVisibility !==
                          AssessmentQuestionResponseVisibility.HiddenFromSubject && {
                          label: `Self ${label("review")} only`,
                          description: `Only the subject of the ${label(
                            "review",
                          )} answers this question`,
                          value: AssessmentQuestionResponses.SelfAssessmentOnly,
                        },
                        {
                          label: "Manager Only",
                          description:
                            "Only the manager of the subject answers this question",
                          value:
                            AssessmentQuestionResponses.ExcludeSelfAssessment,
                        },
                      ])}
                      onChange={(opt) => {
                        onUpdateQuestion({
                          ...talentIndicator,
                          responses: opt.value,
                        });
                      }}
                      aria-label="Responses type select"
                    />
                  ),
                },
                {
                  title: "Response visibility",
                  render: () => (
                    <DeprecatedSelect
                      disabled={
                        !canUpdate ||
                        talentIndicator.responses ===
                          AssessmentQuestionResponses.SelfAssessmentOnly
                      }
                      value={talentIndicator.responseVisibility}
                      options={compact([
                        {
                          label: "Everyone",
                          description: `Responses to this question are visible by anyone who can access the ${label(
                            "review",
                          )}`,
                          value: AssessmentQuestionResponseVisibility.All,
                        },
                        talentIndicator.responses !==
                          AssessmentQuestionResponses.SelfAssessmentOnly && {
                          label: "Hide from subject",
                          description: `Responses to this question, except for their own, are hidden from the subject of the ${label(
                            "review",
                          )}`,
                          value:
                            AssessmentQuestionResponseVisibility.HiddenFromSubject,
                        },
                      ])}
                      onChange={(opt) => {
                        onUpdateQuestion({
                          ...talentIndicator,
                          responseVisibility: opt.value,
                        });
                      }}
                      aria-label="Response visibility type select"
                    />
                  ),
                },
                {
                  title: "Comments",
                  render: () => (
                    <DeprecatedSelect
                      disabled={!canUpdate}
                      value={talentIndicator.comment}
                      options={assessmentQuestionCommentOptions}
                      onChange={(opt) => {
                        onUpdateQuestion({
                          ...talentIndicator,
                          comment: opt.value,
                        });
                      }}
                      aria-label="Mandatory comment select"
                    />
                  ),
                },
              ])}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TalentIndicatorSidebarIndicator;
